export interface DialogueTurn {
  character: string;
  line: string;
}

/**
 * Parse a script into dialogue turns.
 * Supports two common formats:
 *   Block:  CHARACTER NAME\nDialogue text here.
 *   Inline: CHARACTER: Dialogue text here.
 * Strips stage directions in parentheses and ignores scene headings (INT./EXT.).
 */
export function parseScript(scriptText: string): DialogueTurn[] {
  const lines = scriptText.split('\n');
  const turns: DialogueTurn[] = [];

  // Pattern for character names: ALL CAPS, optional parenthetical (V.O.), (CONT'D), optional trailing colon
  const characterLinePattern = /^([A-Z][A-Z0-9\s.'-]{1,30})(?:\s*\([\w.']+\))?\s*:?$/;
  // Inline pattern: CHARACTER: dialogue
  const inlinePattern = /^([A-Z][A-Z0-9\s.'-]{1,30})(?:\s*\([\w.']+\))?\s*:\s*(.+)/;
  // Scene headings to skip
  const sceneHeadingPattern = /^(INT\.|EXT\.|INT\/EXT\.|I\/E\.)\s/;

  let currentCharacter: string | null = null;
  let currentLines: string[] = [];

  const flushTurn = () => {
    if (currentCharacter && currentLines.length > 0) {
      const dialogue = currentLines.join(' ').trim();
      // Strip parenthetical stage directions like (crosses to door)
      const cleaned = dialogue.replace(/\(([^)]*)\)/g, '').replace(/\s{2,}/g, ' ').trim();
      if (cleaned) {
        turns.push({ character: currentCharacter, line: cleaned });
      }
    }
    currentLines = [];
  };

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;

    // Skip scene headings
    if (sceneHeadingPattern.test(line)) continue;

    // Check inline format first: CHARACTER: dialogue
    const inlineMatch = line.match(inlinePattern);
    if (inlineMatch && !line.includes('(') && inlineMatch[1].length <= 25) {
      // Avoid false positives: skip lines that look like stage directions
      flushTurn();
      currentCharacter = inlineMatch[1].trim();
      currentLines = [inlineMatch[2]];
      flushTurn();
      currentCharacter = null;
      continue;
    }

    // Check block format: standalone character name line
    const charMatch = line.match(characterLinePattern);
    if (charMatch && !sceneHeadingPattern.test(line)) {
      const name = charMatch[1].trim();
      // Must be at least 2 chars and not look like a normal sentence
      if (name.length >= 2 && name === name.toUpperCase()) {
        flushTurn();
        currentCharacter = name;
        continue;
      }
    }

    // Regular dialogue line
    if (currentCharacter) {
      currentLines.push(line);
    }
  }

  flushTurn();
  return turns;
}

/**
 * Extract unique character names from parsed dialogue turns.
 */
export function getCharacters(turns: DialogueTurn[]): string[] {
  return [...new Set(turns.map(t => t.character))];
}
